/**
 * The words on the Teams pages, generated from the numbers.
 *
 * Deterministic templates, no model in the loop: the same data always reads
 * the same way, and every sentence here is covered by story.test.ts.
 */

import type { TeamSignal } from "@aura/shared";
import {
  capitalize,
  countWord,
  fmtHours,
  fmtNum,
  inPeriod,
  joinList,
  plural,
  priorName,
} from "~/lib/format";
import type { MomentumRow } from "./model";

/** A slow-review clause needs the slowest team at least this far over the org median. */
export const SLOW_REVIEW_RATIO = 1.25;

/** A team has to move at least this many points of pace against the org to be named. */
export const MOVE_THRESHOLD = 15;

export interface TeamLine {
  id: string;
  name: string;
  prsMerged: number;
  prevPrsMerged: number;
  medianHoursToFirstReview: number | null;
}

export interface TeamsStoryInput {
  periodLabel: string;
  prevLabel: string;
  teams: TeamLine[];
  orgMedianHoursToFirstReview: number | null;
  /** People whose work is split across teams. Null when the API doesn't say. */
  sharedCount: number | null;
  unassignedCount: number;
  /** The org's change in merges per person per working day, whole percent. */
  orgDailyChange?: number | null;
  /** Holiday names in each window. */
  holidays?: { current: string[]; previous: string[] };
  /** Teams by ground gained on the org (model.momentumRows). */
  momentum?: MomentumRow[];
}

/** The team that merged the most, ties broken by name. Null when nobody merged. */
export function topMerger(teams: TeamLine[]): TeamLine | null {
  const sorted = [...teams]
    .filter((t) => t.prsMerged > 0)
    .sort((a, b) => b.prsMerged - a.prsMerged || a.name.localeCompare(b.name));
  return sorted[0] ?? null;
}

/**
 * The team that waited longest for a first review, when that wait is worth a
 * headline: at least two teams measured and the slowest well over the org
 * median.
 */
export function slowestReview(teams: TeamLine[], orgMedian: number | null): TeamLine | null {
  const measured = teams
    .filter((t) => t.medianHoursToFirstReview !== null)
    .sort(
      (a, b) =>
        (b.medianHoursToFirstReview ?? 0) - (a.medianHoursToFirstReview ?? 0) ||
        a.name.localeCompare(b.name),
    );
  const slow = measured[0];
  if (!slow || measured.length < 2) return null;
  if (orgMedian !== null && (slow.medianHoursToFirstReview ?? 0) < orgMedian * SLOW_REVIEW_RATIO) {
    return null;
  }
  return slow;
}

/** Ids of the teams the headline names, so figures color only those. */
export function headlineTeamIds(s: TeamsStoryInput): { top: string | null; slow: string | null } {
  return {
    top: topMerger(s.teams)?.id ?? null,
    slow: slowestReview(s.teams, s.orgMedianHoursToFirstReview)?.id ?? null,
  };
}

export function buildTeamsHeadline(s: TeamsStoryInput): string {
  const when = inPeriod(s.periodLabel);
  if (s.teams.length === 0) return "No teams worked out yet";
  const top = topMerger(s.teams);
  if (!top) return `No team merged a PR ${when} yet`;
  const slow = slowestReview(s.teams, s.orgMedianHoursToFirstReview);
  if (slow && slow.id === top.id) {
    return `${top.name} merged the most ${when} but waited longest for a first review`;
  }
  if (slow) return `${top.name} merged the most ${when}; ${slow.name} waited longest for a first review`;
  return `${top.name} merged the most ${when}, with ${plural(top.prsMerged, "PR")}`;
}

export function buildTeamsDek(s: TeamsStoryInput): string {
  const when = inPeriod(s.periodLabel);
  const prior = priorName(s.prevLabel);
  if (s.teams.length === 0) {
    return "Teams are worked out from JIRA projects and repo activity once there is work to read.";
  }
  const parts: string[] = [
    `${capitalize(countWord(s.teams.length))} ${s.teams.length === 1 ? "team" : "teams"}, worked out from JIRA projects and repo activity.`,
  ];
  if (s.sharedCount !== null) {
    parts.push(
      s.sharedCount === 0
        ? "Nobody splits their work across teams."
        : `${plural(s.sharedCount, "person", "people")} ${s.sharedCount === 1 ? "splits" : "split"} their work across teams but ${s.sharedCount === 1 ? "counts" : "count"} only on their primary team.`,
    );
  }
  parts.push(
    s.unassignedCount === 0
      ? `Everyone with work ${when} fits a team.`
      : `${plural(s.unassignedCount, "person", "people")} with work ${when} ${s.unassignedCount === 1 ? "fits" : "fit"} no team.`,
  );
  const merged = s.teams.reduce((n, t) => n + t.prsMerged, 0);
  const prev = s.teams.reduce((n, t) => n + t.prevPrsMerged, 0);
  parts.push(
    `Together they merged ${plural(merged, "PR")}, against ${fmtNum(prev)} over ${prior}.`,
  );
  const pace = paceSentence(s);
  if (pace) parts.push(pace);
  const movers = moversSentence(s.momentum ?? []);
  if (movers) parts.push(movers);
  return parts.join(" ");
}

/** The org against itself per working day, with the holidays that explain it. */
function paceSentence(s: TeamsStoryInput): string | null {
  const d = s.orgDailyChange;
  if (d === null || d === undefined) return null;
  const prior = priorName(s.prevLabel);
  const how = d === 0 ? "the same as" : `${fmtNum(Math.abs(d))}% ${d > 0 ? "more" : "less"} than`;
  let out = `Per person and working day the org merged ${how} in ${prior}`;
  const prev = s.holidays?.previous ?? [];
  const cur = s.holidays?.current ?? [];
  if (prev.length > 0) out += `, which had ${joinList(prev)} off`;
  if (cur.length > 0) out += `; ${joinList(cur)} ${cur.length === 1 ? "falls" : "fall"} ${inPeriod(s.periodLabel)}`;
  return `${out}.`;
}

/** The biggest gain and the biggest slide against the org, when they're big enough. */
function moversSentence(rows: MomentumRow[]): string | null {
  const moved = rows.filter((r) => r.change !== null && r.before !== null);
  const gained = moved.find((r) => (r.change ?? 0) >= MOVE_THRESHOLD);
  const lost = [...moved].reverse().find((r) => (r.change ?? 0) <= -MOVE_THRESHOLD);
  const clauses: string[] = [];
  if (gained) {
    clauses.push(
      `${gained.name} gained the most on the org, from ${fmtNum(gained.before)}% to ${fmtNum(gained.now)}% of its pace per person`,
    );
  }
  if (lost) {
    clauses.push(`${lost.name} slipped from ${fmtNum(lost.before)}% to ${fmtNum(lost.now)}%`);
  }
  return clauses.length > 0 ? `${clauses.join("; ")}.` : null;
}

export interface TeamStoryInput {
  name: string;
  periodLabel: string;
  prevLabel: string;
  prsMerged: number;
  prevPrsMerged: number;
  headcount: number;
  sharedCount: number;
  perPerson: number | null;
  orgPerPerson: number | null;
  medianHoursToFirstReview: number | null;
  orgMedianHoursToFirstReview: number | null;
  leads: string[];
  signal: TeamSignal;
  jiraProjectKey: string | null;
  repoCount: number;
  /** Pace index (100 = the org) now and last period. */
  paceNow?: number | null;
  paceBefore?: number | null;
  /** Median contributor score on the team and across the org. */
  score?: number | null;
  orgScore?: number | null;
}

export function buildTeamHeadline(s: TeamStoryInput): string {
  const when = inPeriod(s.periodLabel);
  const prior = priorName(s.prevLabel);
  let lead: string;
  if (s.prsMerged === 0 && s.prevPrsMerged === 0) {
    lead = `${s.name} has no merged PRs ${when} yet`;
  } else if (s.prevPrsMerged === 0) {
    lead = `${s.name} merged ${plural(s.prsMerged, "PR")} ${when}, from none over ${prior}`;
  } else if (s.prsMerged === s.prevPrsMerged) {
    lead = `${s.name} merged ${plural(s.prsMerged, "PR")} ${when}, the same as ${prior}`;
  } else {
    const d = s.prsMerged - s.prevPrsMerged;
    lead = `${s.name} merged ${plural(s.prsMerged, "PR")} ${when}, ${d > 0 ? "up" : "down"} ${fmtNum(Math.abs(d))} from ${prior}`;
  }
  const wait = s.medianHoursToFirstReview;
  const org = s.orgMedianHoursToFirstReview;
  if (wait !== null && org !== null && org > 0 && wait >= org * SLOW_REVIEW_RATIO) {
    return `${lead}; first reviews took ${fmtHours(wait)} against an org median of ${fmtHours(org)}`;
  }
  return lead;
}

export function buildTeamDek(s: TeamStoryInput): string {
  const parts: string[] = [];
  parts.push(
    `${plural(s.headcount, "person", "people")} on the team${
      s.sharedCount > 0 ? `, ${fmtNum(s.sharedCount)} of them also working for other teams` : ""
    }.`,
  );
  if (s.perPerson !== null) {
    parts.push(
      `Per active person that is ${fmtNum(s.perPerson)} merged ${s.perPerson === 1 ? "PR" : "PRs"}${
        s.orgPerPerson !== null ? `, against an org median of ${fmtNum(s.orgPerPerson)}` : ""
      }.`,
    );
  }
  if (s.paceNow !== null && s.paceNow !== undefined) {
    parts.push(
      `That is ${fmtNum(s.paceNow)}% of the org's pace per person${
        s.paceBefore !== null && s.paceBefore !== undefined
          ? `, from ${fmtNum(s.paceBefore)}% over ${priorName(s.prevLabel)}`
          : ""
      }.`,
    );
  }
  if (s.score !== null && s.score !== undefined) {
    parts.push(
      `The middle person on the team scores ${fmtNum(s.score)}${
        s.orgScore !== null && s.orgScore !== undefined ? `, against ${fmtNum(s.orgScore)} across the org` : ""
      }.`,
    );
  }
  if (s.leads.length > 0) parts.push(`Led by ${joinList(s.leads)}.`);
  parts.push(sourceSentence(s));
  return parts.join(" ");
}

/** Where the team came from, in plain words. */
export function sourceSentence(s: {
  signal: TeamSignal;
  jiraProjectKey: string | null;
  repoCount: number;
}): string {
  const repos = s.repoCount > 0 ? plural(s.repoCount, "repo") : null;
  switch (s.signal) {
    case "jira_project":
      return `Worked out from the ${s.jiraProjectKey ?? "team's"} JIRA project${repos ? ` and ${repos}` : ""}.`;
    case "repo_cluster":
      return `Worked out from who commits to ${repos ?? "the same repos"}.`;
    case "review_cluster":
      return "Worked out from who reviews whose code.";
    case "manual":
      return "Set by an admin.";
  }
}
